<template>
  <div class="termin">
    <!-- ============ Hero ============ -->
    <section class="termin-hero">
      <div class="lb-container lb-container--narrow termin-hero__inner">
        <SectionLabel text="Persönliche Telefonberatung" />
        <h1>
          Termin zur
          <span class="hero__highlight">Sprechstunde</span> sichern
        </h1>
        <p v-if="intro" class="termin-intro">{{ intro }}</p>
        <p v-else class="termin-intro">
          Wählen Sie einen freien Termin — kostenlose Telefonberatung
          in 10 bis 15 Minuten, klare Empfehlung für Ihre Zahnzusatzversicherung.
        </p>

        <ul class="termin-hero__meta">
          <li><span class="check">✔</span> 100 % kostenlos</li>
          <li><span class="check">✔</span> Keine Vorbereitung nötig</li>
          <li><span class="check">✔</span> Sie entscheiden in Ruhe</li>
        </ul>
      </div>
    </section>

    <!-- ============ Kalender ============ -->
    <section class="lb-section lb-section--cream termin-cal">
      <div class="lb-container">
        <div class="termin-pager">
          <button
            class="termin-pager__btn"
            :disabled="pageIndex === 0"
            @click="changePage(-1)"
          >
            <span aria-hidden="true">←</span> Vorherige
          </button>

          <div class="termin-pager__range" v-if="range">
            <span class="termin-pager__label">Zeitraum</span>
            <span class="termin-pager__date">{{ formatRange(range.from, range.to) }}</span>
          </div>

          <button class="termin-pager__btn" @click="changePage(1)">
            Nächste <span aria-hidden="true">→</span>
          </button>
        </div>

        <div v-if="loading" class="termin-loading-inline">
          <div class="termin-loading__spinner"></div>
          <span>Lade Termine…</span>
        </div>

        <div v-if="!loading && !hasSlots" class="termin-empty-banner">
          <SparkleIcon :size="20" color="#F59E0B" />
          <span>
            {{ emptyText || 'In diesem Zeitraum sind aktuell keine Termine verfügbar. Bitte wählen Sie den nächsten Zeitraum.' }}
          </span>
        </div>

        <div class="termin-grid" :class="{ 'termin-grid--loading': loading }">
            <div
              v-for="day in days"
              :key="day.date"
              class="termin-day"
              :class="{
                'termin-day--empty':    !day.slots || !day.slots.length,
                'termin-day--weekend':  isWeekend(day.date),
                'termin-day--today':    isToday(day.date),
              }"
            >
              <div class="termin-day__head">
                <div class="termin-day__weekday">{{ weekdayLabel(day.date) }}</div>
                <div class="termin-day__date">{{ formatDayShort(day.date) }}</div>
                <span v-if="isToday(day.date)" class="termin-day__badge">heute</span>
              </div>

              <div v-if="day.slots && day.slots.length" class="termin-day__slots">
                <button
                  v-for="slot in day.slots"
                  :key="slot.slot_id"
                  class="termin-slot"
                  :class="{
                    'termin-slot--booked': slot.is_booked,
                    'termin-slot--free':  !slot.is_booked,
                  }"
                  :disabled="slot.is_booked"
                  @click="openBooking(slot)"
                >
                  <span class="termin-slot__time">{{ slot.time }}</span>
                  <span class="termin-slot__state">
                    {{ slot.is_booked ? 'belegt' : 'frei' }}
                  </span>
                </button>
              </div>

              <div v-else class="termin-day__noslots">
                <span>—</span>
                <small>keine Termine</small>
              </div>
            </div>
          </div>
      </div>
    </section>

    <!-- ============ Info-Box: Versicherungsprinzip ============ -->
    <section class="lb-section termin-info">
      <div class="lb-container lb-container--narrow">
        <aside class="termin-info__box">
          <div class="termin-info__badge">Wichtig</div>
          <h3 class="termin-info__title">Versicherungsprinzip</h3>
          <p class="termin-info__lead">
            <strong>Bereits eingetretene Defekte können nicht mehr versichert werden.</strong>
          </p>
          <p>
            Was bedeutet das genau? Es kann keine Zahnzusatzversicherung mehr für
            bereits eingetretene Defekte abgeschlossen werden, um genau dafür eine
            Erstattung / Leistung zu erhalten.
          </p>
          <p>
            Bereits eingetretene Defekte, angeratene Behandlungen, angeratene Maßnahmen,
            geplante Behandlungen sowie Behandlungen, die bereits am Laufen sind,
            können nicht mehr versichert werden.
          </p>
          <p class="termin-info__example">
            <em>Beispiel:</em>
            Meine Zahnkrone ist defekt. Für diese Behandlung habe ich bereits einen
            Heil- und Kostenplan erhalten.
          </p>
        </aside>
      </div>
    </section>

    <!-- ============ Buchungs-Modal ============ -->
    <div v-if="bookingSlot" class="termin-modal" @click.self="closeBooking">
      <div class="termin-modal__inner">
        <button class="termin-modal__close" aria-label="Schließen" @click="closeBooking">×</button>

        <SectionLabel text="Ihr Rückruf-Termin" />
        <h2>Jetzt Ihren Rückruf-Termin wählen</h2>
        <p class="termin-modal__badge-line">
          <span class="termin-modal__badge">100 % kostenlos</span>
        </p>
        <p class="termin-modal__when">
          {{ formatDateLong(bookingSlot.date) }} um <strong>{{ bookingSlot.time }} Uhr</strong>
          <span class="termin-modal__dur">({{ bookingSlot.duration }} Min.)</span>
        </p>

        <form v-if="!submitted" class="termin-form" @submit.prevent="submitBooking" novalidate>
          <label class="termin-form__field">
            <span>Ihre Telefonnummer *</span>
            <input v-model="form.phone" type="tel" required autocomplete="tel" />
          </label>

          <label class="termin-form__field">
            <span>Ihre E-Mail *</span>
            <input v-model="form.email" type="email" required autocomplete="email" />
          </label>

          <label class="termin-form__field">
            <span>Ihr Vor- und Nachname *</span>
            <input v-model="form.name" type="text" required autocomplete="name" />
          </label>

          <div class="termin-form__row">
            <label class="termin-form__field">
              <span>Geburtsdatum *</span>
              <input
                v-model="form.birthdate"
                type="text"
                inputmode="numeric"
                required
                placeholder="z.B. 01.03.1966"
                autocomplete="bday"
                pattern="\d{2}\.\d{2}\.\d{4}"
                title="Bitte im Format TT.MM.JJJJ eingeben (z.B. 01.03.1966)"
              />
            </label>

            <label class="termin-form__field">
              <span>Geschlecht *</span>
              <select v-model="form.gender" required>
                <option value="" disabled>Bitte wählen…</option>
                <option value="weiblich">weiblich</option>
                <option value="männlich">männlich</option>
                <option value="divers">divers</option>
              </select>
            </label>
          </div>

          <fieldset class="termin-form__fieldset">
            <legend>Ihr Versicherungsstatus *</legend>
            <p class="termin-form__legend-hint">Bitte eine Option auswählen.</p>
            <label
              v-for="opt in insuranceOptions"
              :key="opt.value"
              class="termin-form__radio"
            >
              <input
                type="radio"
                name="insurance"
                :value="opt.value"
                v-model="form.insurance"
                required
              />
              <span>{{ opt.label }}</span>
            </label>
          </fieldset>

          <label class="termin-form__field">
            <span>Nachricht (optional)</span>
            <textarea v-model="form.message" rows="3"
              placeholder="Gibt es etwas, das ich vorab wissen sollte?"></textarea>
          </label>

          <div class="termin-form__consent">
            <label class="termin-form__check">
              <input type="checkbox" v-model="form.consent" required />
              <span>
                Ja, ich habe die Erstinformation für Kunden erhalten.
              </span>
            </label>
            <p class="termin-form__consent-link">
              <button type="button" class="termin-link-btn" @click="showErstinfo = true">
                Erstinformationen für Versicherungsmakler gemäß § 15 VersVermV jetzt anzeigen oder herunterladen.
              </button>
            </p>
            <p v-if="consentError" class="termin-form__consent-error">
              Sie müssen den Bedingungen zustimmen, um fortzufahren.
            </p>
          </div>

          <div v-if="submitError" class="termin-form__error">{{ submitError }}</div>

          <button class="termin-cta" type="submit" :disabled="submitting">
            <span v-if="submitting">Wird gesendet…</span>
            <span v-else>Jetzt Telefonberatung anfordern →</span>
          </button>

          <p class="termin-form__hint">
            Mit dem Anfordern stimmen Sie der Verarbeitung Ihrer Daten zur
            Terminabwicklung zu.
          </p>
        </form>

        <div v-else class="termin-form__success">
          <SparkleIcon :size="40" color="#F59E0B" />
          <h3>Vielen Dank!</h3>
          <p>Ihre Anfrage wurde gespeichert.</p>
          <p>Sie erhalten sofort Ihre Bestätigung per Email / per SMS / Chat.</p>
          <button class="termin-cta termin-cta--ghost" @click="closeBooking">
            Schließen
          </button>
        </div>
      </div>
    </div>

    <!-- ============ Erstinformation-Modal ============ -->
    <div v-if="showErstinfo" class="termin-modal" @click.self="showErstinfo = false">
      <div class="termin-modal__inner termin-modal__inner--info">
        <button class="termin-modal__close" aria-label="Schließen" @click="showErstinfo = false">×</button>
        <SectionLabel text="Erstinformation" />
        <h2>Erstinformation gemäß § 15 VersVermV</h2>
        <p class="termin-modal__hint">
          Die vollständige PDF-Erstinformation wird hier zum Anzeigen und
          Herunterladen bereitgestellt.
        </p>
        <p>
          <strong>Kilian Link · LinkBeratung</strong><br />
          Lohstr. 56, 81543 München-Untergiesing<br />
          E-Mail:
          <a href="mailto:kontakt@linkberatung.de">kontakt@linkberatung.de</a>
        </p>
        <p>
          Versicherungsvermittler gemäß § 34d Abs. 1 GewO (Bundesrepublik Deutschland).<br />
          Registernummer: § 34d D-35B4-VQTG7-49<br />
          Registerbehörde: DIHK – Deutscher Industrie- und Handelskammertag e. V.,
          Breite Straße 29, 10178 Berlin
        </p>
        <p>
          Aufsichtsbehörde: Industrie- und Handelskammer für München und
          Oberbayern, Balanstraße 55–59, 81541 München.
        </p>
        <p class="termin-modal__placeholder">
          Hinweis: Die finale PDF-Datei wird in Kürze hier hinterlegt.
        </p>
        <button class="termin-cta termin-cta--ghost" @click="showErstinfo = false">
          Schließen
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import BaseCalls from "@/services/BaseCalls.js";
import SectionLabel from "@/components/ui/SectionLabel.vue";
import SparkleIcon from "@/components/ui/SparkleIcon.vue";

export default {
  name: "TerminIndex",
  components: { SectionLabel, SparkleIcon },
  data() {
    return {
      pageIndex: 0,
      days: [],
      range: null,
      hasSlots: false,
      intro: "",
      emptyText: "",
      loading: false,

      bookingSlot: null,
      form: {
        name: "",
        email: "",
        phone: "",
        birthdate: "",
        gender: "",
        insurance: "",
        message: "",
        consent: false,
      },
      submitting: false,
      submitted: false,
      submitError: "",
      consentError: false,
      showErstinfo: false,
      insuranceOptions: [
        { value: "gesetzliche_krankenkasse",         label: "Gesetzliche Krankenkasse" },
        { value: "heilfuersorge",                    label: "Heilfürsorge" },
        { value: "gesetzliche_krankenkasse_beihilfe", label: "Gesetzliche Krankenkasse und Beihilfe" },
        { value: "freie_heilfuersorge_beihilfe",      label: "Freie Heilfürsorge & Beihilfe" },
      ],
    };
  },
  mounted() {
    // Vorab Fallback rendern, damit beim ersten Paint
    // schon Kacheln sichtbar sind (auch bevor das Backend antwortet).
    this.applyFallback();
    this.loadPage();
  },
  methods: {
    async loadPage() {
      this.loading = true;
      try {
        const r = await BaseCalls.terminGet({ action: "page", id: this.pageIndex });
        const page = r?.data?.page;
        if (page && Array.isArray(page.days) && page.days.length > 0) {
          this.days = page.days;
          this.range = page.range || this.buildFallbackRange(this.pageIndex);
          this.hasSlots = !!page.has_slots;
          this.intro = page.texts?.intro || "";
          this.emptyText = page.texts?.empty || "";
        } else {
          // Backend hat geantwortet, aber keine Tage geliefert
          // → Fallback (gleicher Zeitraum, leere Slots).
          this.applyFallback();
          if (page) {
            this.intro = page.texts?.intro || this.intro;
            this.emptyText = page.texts?.empty || this.emptyText;
          }
        }
      } catch (e) {
        // Backend nicht erreichbar → Fallback weiterhin anzeigen.
        this.applyFallback();
      } finally {
        this.loading = false;
      }
    },
    applyFallback() {
      this.days = this.buildFallbackDays(this.pageIndex);
      this.range = this.buildFallbackRange(this.pageIndex);
      this.hasSlots = false;
    },
    buildFallbackDays(pageIndex) {
      const daysPerPage = 14;
      const start = this.startOfToday();
      start.setDate(start.getDate() + pageIndex * daysPerPage);
      const out = [];
      for (let i = 0; i < daysPerPage; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        out.push({
          date: this.toIsoDate(d),
          weekday: String(((d.getDay() + 6) % 7) + 1), // 1=Mo..7=So
          slots: [],
        });
      }
      return out;
    },
    buildFallbackRange(pageIndex) {
      const daysPerPage = 14;
      const start = this.startOfToday();
      start.setDate(start.getDate() + pageIndex * daysPerPage);
      const end = new Date(start);
      end.setDate(start.getDate() + daysPerPage - 1);
      return {
        from: this.toIsoDate(start),
        to:   this.toIsoDate(end),
      };
    },
    startOfToday() {
      const t = new Date();
      t.setHours(0, 0, 0, 0);
      return t;
    },
    toIsoDate(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },
    changePage(delta) {
      const next = Math.max(0, this.pageIndex + delta);
      if (next === this.pageIndex) return;
      this.pageIndex = next;
      // sofort Fallback-Tage rendern → kein leerer Zustand zwischendurch
      this.applyFallback();
      this.loadPage();
    },
    emptyForm() {
      return {
        name: "",
        email: "",
        phone: "",
        birthdate: "",
        gender: "",
        insurance: "",
        message: "",
        consent: false,
      };
    },
    openBooking(slot) {
      if (slot.is_booked) return;
      this.bookingSlot = slot;
      this.submitted = false;
      this.submitError = "";
      this.consentError = false;
      this.form = this.emptyForm();
    },
    closeBooking() {
      this.bookingSlot = null;
      if (this.submitted) this.loadPage();
    },
    insuranceLabel(value) {
      const opt = this.insuranceOptions.find((o) => o.value === value);
      return opt ? opt.label : value;
    },
    buildBookingMessage() {
      const lines = [];
      if (this.form.birthdate) lines.push(`Geburtsdatum: ${this.form.birthdate}`);
      if (this.form.gender)    lines.push(`Geschlecht: ${this.form.gender}`);
      if (this.form.insurance) lines.push(`Versicherungsstatus: ${this.insuranceLabel(this.form.insurance)}`);
      const note = (this.form.message || "").trim();
      if (note) {
        lines.push("");
        lines.push(note);
      }
      return lines.join("\n").trim();
    },
    async submitBooking() {
      if (!this.bookingSlot) return;
      if (!this.form.consent) {
        this.consentError = true;
        return;
      }
      this.consentError = false;
      this.submitting = true;
      this.submitError = "";
      try {
        const r = await BaseCalls.terminPost({
          action: "book",
          booking_slot_id: this.bookingSlot.slot_id,
          booking_name: this.form.name,
          booking_email: this.form.email,
          booking_phone: this.form.phone,
          booking_birthdate: this.form.birthdate,
          booking_message: this.buildBookingMessage(),
        });
        if (r?.data?.success) {
          this.submitted = true;
        } else {
          this.submitError = r?.data?.message || "Buchung fehlgeschlagen.";
        }
      } catch (e) {
        this.submitError = "Netzwerk-/Serverfehler.";
      } finally {
        this.submitting = false;
      }
    },
    formatDayShort(iso) {
      if (!iso) return "";
      const [, m, d] = iso.split("-");
      return `${d}.${m}.`;
    },
    formatDateLong(iso) {
      if (!iso) return "";
      const dt = new Date(iso + "T00:00:00");
      const wd = ["Sonntag","Montag","Dienstag","Mittwoch","Donnerstag","Freitag","Samstag"][dt.getDay()];
      const mn = ["Jan","Feb","März","Apr","Mai","Juni","Juli","Aug","Sep","Okt","Nov","Dez"][dt.getMonth()];
      return `${wd}, ${dt.getDate()}. ${mn} ${dt.getFullYear()}`;
    },
    formatRange(a, b) {
      const fmt = (iso) => {
        const [y, m, d] = iso.split("-");
        return `${d}.${m}.${y}`;
      };
      return `${fmt(a)} – ${fmt(b)}`;
    },
    weekdayLabel(iso) {
      if (!iso) return "";
      const dt = new Date(iso + "T00:00:00");
      return ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"][dt.getDay()];
    },
    isWeekend(iso) {
      if (!iso) return false;
      const dt = new Date(iso + "T00:00:00");
      const d = dt.getDay();
      return d === 0 || d === 6;
    },
    isToday(iso) {
      if (!iso) return false;
      const t = new Date();
      const today = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
      return iso === today;
    },
  },
};
</script>

<style scoped>
/* ============== HERO ============== */
.termin-hero {
  background:
    radial-gradient(900px 500px at 80% -10%, rgba(108, 76, 241, 0.08), transparent 60%),
    radial-gradient(700px 400px at -10% 30%, rgba(245, 158, 11, 0.08), transparent 60%),
    #fff;
  padding: 72px 0 36px;
  text-align: center;
}

.termin-hero__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}

.termin-hero__inner h1 {
  margin: 12px 0 12px;
}

.hero__highlight {
  background: linear-gradient(180deg, transparent 60%, rgba(245, 158, 11, 0.35) 60%);
  padding: 0 4px;
}

.termin-intro {
  font-size: clamp(17px, 1.4vw, 20px);
  color: var(--lb-ink-soft);
  max-width: 680px;
  margin: 0;
}

.termin-hero__meta {
  list-style: none;
  padding: 0;
  margin: 10px 0 0;
  display: flex;
  gap: 22px;
  flex-wrap: wrap;
  justify-content: center;
  font-size: 15px;
  color: var(--lb-ink);
}

.check {
  color: var(--lb-cta);
  font-weight: 900;
  margin-right: 4px;
}

/* ============== CAL ============== */
.termin-cal {
  padding-top: 56px;
}

.termin-pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 28px;
  flex-wrap: wrap;
}

.termin-pager__btn {
  font-family: "Lato", sans-serif;
  font-weight: 700;
  font-size: 14px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  background: #fff;
  color: var(--lb-ink);
  border: 1.5px solid var(--lb-line);
  border-radius: 999px;
  padding: 10px 20px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.termin-pager__btn:hover:not(:disabled) {
  border-color: var(--lb-accent);
  color: var(--lb-accent);
}

.termin-pager__btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.termin-pager__range {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.termin-pager__label {
  font-family: "Caveat", cursive;
  font-size: 18px;
  color: var(--lb-cta);
  line-height: 1;
}

.termin-pager__date {
  font-family: "Playfair Display", serif;
  font-size: 20px;
  font-weight: 700;
  color: var(--lb-ink);
}

/* Grid */
.termin-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 10px;
}

@media (max-width: 1100px) {
  .termin-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media (max-width: 760px) {
  .termin-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 420px) {
  .termin-grid {
    grid-template-columns: 1fr;
  }
}

.termin-day {
  background: #fff;
  border: 1px solid var(--lb-line);
  border-radius: 14px;
  padding: 14px 12px;
  min-height: 150px;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.termin-day:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 30px rgba(11, 31, 59, 0.06);
}

.termin-day--weekend {
  background: #faf6ee;
}

.termin-day--empty {
  background: #fdfbf6;
  border-style: dashed;
}

.termin-day--empty:hover {
  transform: none;
  box-shadow: none;
}

.termin-day--today {
  border-color: var(--lb-cta);
  border-width: 2px;
  background: #fff;
  box-shadow: 0 8px 20px rgba(245, 158, 11, 0.12);
}

.termin-day__head {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.termin-day__weekday {
  font-family: "Playfair Display", serif;
  font-weight: 800;
  font-size: 16px;
  color: var(--lb-ink);
}

.termin-day__date {
  color: var(--lb-mute);
  font-size: 13px;
  font-weight: 600;
}

.termin-day__badge {
  margin-left: auto;
  background: var(--lb-cta);
  color: #fff;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 999px;
}

.termin-day__slots {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.termin-slot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1.5px solid transparent;
  background: #f6f1e6;
  color: var(--lb-ink);
  cursor: pointer;
  font-family: "Lato", sans-serif;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.15s ease;
}

.termin-slot__time {
  font-weight: 700;
}

.termin-slot__state {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.7;
}

.termin-slot--free:hover {
  background: var(--lb-cta);
  color: #fff;
  border-color: var(--lb-cta);
  transform: translateX(2px);
}

.termin-slot--free:hover .termin-slot__state {
  opacity: 1;
}

.termin-slot--booked {
  background: #fdecea;
  color: #b91c1c;
  border-color: #f5c6c0;
  cursor: not-allowed;
  text-decoration: line-through;
}

.termin-slot--booked .termin-slot__state {
  color: #b91c1c;
  opacity: 1;
}

.termin-day__noslots {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: var(--lb-mute);
  text-align: center;
  padding: 12px 4px;
}

.termin-day__noslots span {
  font-family: "Playfair Display", serif;
  font-size: 24px;
  line-height: 1;
}

.termin-day__noslots small {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-top: 4px;
  font-weight: 600;
}

/* Empty banner */
.termin-empty-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  border: 1.5px solid var(--lb-line);
  border-radius: 14px;
  padding: 16px 20px;
  margin-bottom: 22px;
  color: var(--lb-ink);
  font-size: 15px;
}

/* Inline-Loading (Grid bleibt sichtbar) */
.termin-loading-inline {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  margin-bottom: 16px;
  background: #fff;
  border: 1.5px dashed var(--lb-line);
  border-radius: 12px;
  color: var(--lb-ink-soft);
  font-size: 14px;
  font-weight: 600;
  width: fit-content;
}

.termin-loading__spinner {
  width: 18px;
  height: 18px;
  border: 2.5px solid var(--lb-line);
  border-top-color: var(--lb-cta);
  border-radius: 50%;
  animation: termin-spin 0.7s linear infinite;
}

@keyframes termin-spin {
  to {
    transform: rotate(360deg);
  }
}

.termin-grid--loading {
  opacity: 0.6;
  pointer-events: none;
  transition: opacity 0.2s ease;
}

/* ============== INFO-BOX ============== */
.termin-info {
  padding: 56px 0 24px;
}

.termin-info__box {
  background: #fff;
  border: 1px solid var(--lb-line);
  border-left: 6px solid var(--lb-cta);
  border-radius: 16px;
  padding: 28px 32px;
  box-shadow: 0 16px 36px rgba(11, 31, 59, 0.06);
}

.termin-info__badge {
  display: inline-block;
  background: var(--lb-cta);
  color: #fff;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 12px;
}

.termin-info__title {
  margin: 0 0 14px;
  font-size: clamp(22px, 2.4vw, 28px);
}

.termin-info__lead {
  font-size: 17px;
  color: var(--lb-ink);
  margin: 0 0 14px;
}

.termin-info__box p {
  color: var(--lb-ink-soft);
  font-size: 16px;
  line-height: 1.6;
}

.termin-info__example {
  background: var(--lb-cream);
  border-radius: 10px;
  padding: 14px 16px;
  margin-top: 12px;
  font-size: 15px;
}

.termin-info__example em {
  color: var(--lb-cta);
  font-style: normal;
  font-weight: 700;
  margin-right: 4px;
}

/* ============== MODAL ============== */
.termin-modal {
  position: fixed;
  inset: 0;
  background: rgba(11, 31, 59, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 80;
  padding: 20px;
  backdrop-filter: blur(4px);
  animation: termin-fade-in 0.2s ease;
}

@keyframes termin-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.termin-modal__inner {
  background: #fff;
  border-radius: 20px;
  padding: 36px 32px 32px;
  max-width: 520px;
  width: 100%;
  position: relative;
  box-shadow: 0 40px 80px rgba(11, 31, 59, 0.3);
  max-height: 90vh;
  overflow-y: auto;
}

.termin-modal__inner h2 {
  margin: 8px 0 12px;
  font-size: clamp(28px, 3.5vw, 38px);
}

.termin-modal__close {
  position: absolute;
  top: 14px;
  right: 18px;
  background: none;
  border: 0;
  font-size: 30px;
  line-height: 1;
  color: var(--lb-mute);
  cursor: pointer;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  transition: background 0.15s ease;
}

.termin-modal__close:hover {
  background: var(--lb-cream);
  color: var(--lb-ink);
}

.termin-modal__when {
  color: var(--lb-ink-soft);
  font-size: 16px;
  margin: 0 0 24px;
  padding-bottom: 18px;
  border-bottom: 1px solid var(--lb-line);
}

.termin-modal__when strong {
  color: var(--lb-cta);
}

.termin-modal__dur {
  color: var(--lb-mute);
  font-size: 14px;
  margin-left: 6px;
}

.termin-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.termin-form__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

@media (max-width: 480px) {
  .termin-form__row {
    grid-template-columns: 1fr;
  }
}

.termin-form__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  color: var(--lb-ink);
}

.termin-form__field input,
.termin-form__field textarea,
.termin-form__field select {
  font-family: "Lato", sans-serif;
  font-size: 16px;
  font-weight: 400;
  padding: 12px 14px;
  border: 1.5px solid var(--lb-line);
  border-radius: 10px;
  background: #fff;
  color: var(--lb-ink);
  transition: border 0.2s ease, box-shadow 0.2s ease;
}

.termin-form__field select {
  cursor: pointer;
  appearance: none;
  background-image:
    linear-gradient(45deg, transparent 50%, var(--lb-ink) 50%),
    linear-gradient(135deg, var(--lb-ink) 50%, transparent 50%);
  background-position:
    calc(100% - 18px) 50%,
    calc(100% - 12px) 50%;
  background-size: 6px 6px;
  background-repeat: no-repeat;
  padding-right: 36px;
}

.termin-form__field input:focus,
.termin-form__field textarea:focus,
.termin-form__field select:focus {
  outline: none;
  border-color: var(--lb-accent);
  box-shadow: 0 0 0 4px rgba(108, 76, 241, 0.12);
}

/* Fieldset (Versicherungsstatus) */
.termin-form__fieldset {
  border: 1.5px solid var(--lb-line);
  border-radius: 12px;
  padding: 16px 18px 14px;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.termin-form__fieldset legend {
  font-size: 13px;
  font-weight: 700;
  color: var(--lb-ink);
  padding: 0 6px;
}

.termin-form__legend-hint {
  font-size: 12px;
  color: var(--lb-mute);
  margin: 0 0 6px;
}

.termin-form__radio {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  color: var(--lb-ink);
  cursor: pointer;
  padding: 4px 0;
}

.termin-form__radio input[type="radio"] {
  width: 18px;
  height: 18px;
  accent-color: var(--lb-cta);
  cursor: pointer;
}

/* Consent block */
.termin-form__consent {
  background: var(--lb-cream);
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.termin-form__check {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  color: var(--lb-ink);
  cursor: pointer;
  line-height: 1.5;
}

.termin-form__check input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: var(--lb-cta);
  margin-top: 2px;
  cursor: pointer;
  flex-shrink: 0;
}

.termin-form__consent-link {
  margin: 0;
  padding-left: 28px;
}

.termin-link-btn {
  background: none;
  border: 0;
  padding: 0;
  color: var(--lb-accent);
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
  line-height: 1.5;
}

.termin-link-btn:hover {
  color: var(--lb-ink);
}

.termin-form__consent-error {
  color: #b91c1c;
  font-size: 13px;
  font-weight: 700;
  margin: 4px 0 0;
  padding-left: 28px;
}

/* Modal-Header Extras */
.termin-modal__badge-line {
  margin: 4px 0 14px;
}

.termin-modal__badge {
  display: inline-block;
  background: var(--lb-cta);
  color: #fff;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1px;
  text-transform: uppercase;
}

/* Info-Modal (Erstinformation) */
.termin-modal__inner--info p {
  color: var(--lb-ink-soft);
  font-size: 15px;
  line-height: 1.6;
  margin: 0 0 12px;
}

.termin-modal__inner--info a {
  color: var(--lb-accent);
}

.termin-modal__hint {
  background: var(--lb-cream);
  border-left: 4px solid var(--lb-cta);
  padding: 12px 14px;
  border-radius: 8px;
  font-size: 14px !important;
  color: var(--lb-ink) !important;
}

.termin-modal__placeholder {
  font-style: italic;
  font-size: 13px !important;
  color: var(--lb-mute) !important;
}

.termin-form__error {
  background: #fef2f2;
  color: #b91c1c;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
}

.termin-form__hint {
  font-size: 12px;
  color: var(--lb-mute);
  text-align: center;
  margin: 0;
  line-height: 1.5;
}

.termin-cta {
  background: var(--lb-cta);
  color: #fff;
  border: 0;
  padding: 16px 26px;
  border-radius: 999px;
  font-family: "Lato", sans-serif;
  font-weight: 900;
  font-size: 15px;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 8px 22px rgba(245, 158, 11, 0.35);
}

.termin-cta:hover:not(:disabled) {
  background: var(--lb-cta-dark);
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(245, 158, 11, 0.45);
}

.termin-cta:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.termin-cta--ghost {
  background: transparent;
  color: var(--lb-ink);
  border: 1.5px solid var(--lb-line);
  box-shadow: none;
}

.termin-cta--ghost:hover:not(:disabled) {
  background: var(--lb-cream);
  transform: none;
  box-shadow: none;
}

.termin-form__success {
  text-align: center;
  padding: 24px 8px 12px;
}

.termin-form__success h3 {
  font-family: "Playfair Display", serif;
  font-size: 32px;
  margin: 14px 0 8px;
}

.termin-form__success p {
  color: var(--lb-ink-soft);
  margin: 4px 0;
}

.termin-form__success .termin-cta {
  margin-top: 18px;
}
</style>
